/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { sort, alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { later } from '@ember/runloop';
import { getTablePrintCss } from 'linced-app/utils/table';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import {
  cleanExcelSheetName,
  cleanFileName,
  uniqueSheetName,
} from '@linc-technologies/linced-common/utils/file-names';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import moment from 'moment';

export default class EvacuationController extends Controller {
  @service
  attendanceCodes;

  @service
  session;

  @service
  dates;

  @service
  excel;

  @service
  schoolSchema;

  @service
  strings;

  // mode determines the report mode: 'homerooms' or 'school'
  @tracked
  mode = 'homerooms';

  @action
  setMode(value) {
    this.mode = value;
  }

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  @computed('jurisdiction')
  get showJurisdictionalId() {
    return this.jurisdiction === Jurisdiction.AU_NSW;
  }

  @tracked
  jurisdictionalIdField = null;

  @alias('model.date.date')
  date;

  @computed('date')
  get dateFormatted() {
    return moment(this.date, 'YYYY-MM-DD').format('D MMM YYYY');
  }

  @computed('date', 'dates.structures.[]')
  get day() {
    return this.dates.dayForDate(this.date);
  }

  @computed('day.periods.length')
  get hasMultiplePeriods() {
    let numPeriods = this.get('day.periods.length');
    return !isEmpty(numPeriods) && numPeriods > 1;
  }

  title = 'Evacuation Report';

  @sort('model.homerooms', naturalSort('name'))
  sortedHomerooms;

  @computed('attendanceCodes.allCodes.[]')
  get presentCodes() {
    return this.attendanceCodes.allCodes.reduce((presentCodes, code) => {
      if (code.inClass) {
        presentCodes.push(code.code);
      }
      return presentCodes;
    }, []);
  }

  @computed('attendanceCodes.allCodes.[]')
  get attendanceLabels() {
    return this.attendanceCodes.allCodes.reduce((labels, code) => {
      labels[code.code] = code.label;
      return labels;
    }, {});
  }

  @computed('model.{homerooms.[],attendanceRecords.[]}', 'presentCodes.[]')
  get learners() {
    let recordsByLearner = this.model.attendanceRecords.reduce(
      (map, record) => {
        map[record.person_id] = record;
        return map;
      },
      {},
    );
    return this.get('model.homerooms').reduce((map, homeroom) => {
      if (isEmpty(homeroom.currentEnrolledLearners)) {
        return map;
      }
      map[homeroom.id] = homeroom.currentEnrolledLearners.reduce(
        (learners, member) => {
          let learner = member.get('person');
          let record = recordsByLearner[learner.id];
          if (isEmpty(record)) {
            learners.unmarked.push(learner);
          } else if (this.presentCodes.includes(record.code)) {
            learners.present.push(learner);
          } else {
            learners.absent.push({
              learner,
              record,
            });
          }
          return learners;
        },
        { present: [], absent: [], unmarked: [] },
      );
      return map;
    }, {});
  }

  @computed('learners')
  get wholeSchool() {
    return Object.entries(this.learners).reduce(
      (all, homeroom) => {
        all.present.push(...homeroom[1].present);
        all.absent.push(...homeroom[1].absent);
        all.unmarked.push(...homeroom[1].unmarked);
        return all;
      },
      { present: [], absent: [], unmarked: [] },
    );
  }

  init() {
    super.init(...arguments);
    this.schoolSchema.getJurisdictionalIdField().then((field) => {
      this.jurisdictionalIdField = field;
    });
  }

  @action
  downloadFullXlsx(groupId) {
    let sheets = [];
    let sheetNames = [];

    this.sortedHomerooms.forEach((homeroom) => {
      if (isEmpty(groupId) || groupId === 'all' || homeroom.id === groupId) {
        let sheetName = uniqueSheetName(
          cleanExcelSheetName(homeroom.name),
          sheetNames,
        );

        sheets.push({
          name: sheetName,
          data: this.getExportData(homeroom),
        });
      }
    });

    let title = `${this.title} ${moment().format('YYYY-MM-DD')}`;
    if (!isEmpty(groupId)) {
      let group = findBy(this.sortedHomerooms, 'id', groupId);
      if (!isEmpty(group)) {
        title = `${title} - ${group.name}`;
      }
    }
    let fileName = `${title}.xlsx`;

    this.excel.export(sheets, {
      multiSheet: true,
      fileName: cleanFileName(fileName),
    });
  }

  @action
  downloadWholeSchoolXlsx() {
    let sheets = [];
    if (!isEmpty(this.wholeSchool.present)) {
      sheets.push({
        name: 'Present',
        data: this.getExportList(this.wholeSchool, 'present', [], false),
      });
    }
    if (!isEmpty(this.wholeSchool.absent)) {
      sheets.push({
        name: 'Absent',
        data: this.getExportList(this.wholeSchool, 'absent', [], false),
      });
    }
    if (!isEmpty(this.wholeSchool.unmarked)) {
      sheets.push({
        name: 'Unmarked',
        data: this.getExportList(this.wholeSchool, 'unmarked', [], false),
      });
    }

    let date = moment().format('YYYY-MM-DD');
    let fileName = `${this.title} ${date} - Whole School.xlsx`;

    this.excel.export(sheets, {
      multiSheet: true,
      fileName: cleanFileName(fileName),
    });
  }

  @action
  print(groupId) {
    let printer = window.open(
      'about:blank',
      '_blank',
      'menubar=yes,location=no,resizable=yes,scrollbars=yes,status=no,height=700,width=1024',
    );
    let selector = '.class-list';
    if (!isEmpty(groupId) && groupId !== 'all') {
      selector = `.class-list[data-groupid="${groupId}"]`;
    }
    let classLists = document.querySelectorAll(selector);
    let content = getTablePrintCss(false);

    content += `
      <style>
        .table-tools button { display: none; }
        table { table-layout: fixed; }
        h3 {
          margin-bottom: 6px;
        }
        h4, p {
          margin-top: 6px;
          margin-bottom: 4px;
        }
      </style>
    `;

    classLists.forEach((classList, index) => {
      if (index !== 0) {
        content += `<div style="page-break-before: always"></div>`;
      }

      content += classList.outerHTML;
    });

    printer.document.body.innerHTML = content;

    later(
      this,
      (printer) => {
        printer.print();
        printer.close();
      },
      printer,
      500,
    );
  }

  getExportData(homeroom) {
    if (isEmpty(homeroom)) {
      return [];
    }

    let learners = this.get(`learners.${homeroom.id}`);
    let data = [];

    // Class info
    data.push(
      [`${homeroom.name}`],
      [
        `${homeroom.staffNames} - ${homeroom.currentEnrolledLearners.length} children.`,
      ],
    );

    if (isEmpty(homeroom.currentEnrolledLearners)) {
      // No current students - don't bother trying to generate lists.
      return data;
    }

    ['present', 'absent', 'unmarked'].forEach((list) => {
      this.getExportList(learners, list, data, true);
    });

    return data;
  }

  getExportList(learners, list, data, isHomeroom) {
    if (isEmpty(learners[list])) {
      // No learners of this attendance status; skip.
      return;
    }

    let pluralised = 'student';
    let numStudents = learners[list].length;
    if (numStudents !== 1) {
      pluralised += 's';
    }

    if (isHomeroom) {
      // Add an empty row to separate tables.
      data.push(['']);
    }

    data.push([`${numStudents} ${pluralised} ${list}`]);

    // Column headers
    let headers = ['Last Name', 'First Name', 'Year'];
    if (this.showJurisdictionalId) {
      headers.push(this.get('jurisdictionalIdField.label'));
    }
    let numEmptyColumns = 2;
    switch (list) {
      case 'absent':
        if (this.trackMovements) {
          headers.push('Time out');
        }
        headers.push('Code', 'Reason');
        break;
      default:
        headers.push('Accounted for?', 'Collected by', 'Time');
        numEmptyColumns++;
        break;
    }
    data.push(headers);

    // Data
    learners[list].forEach((learner) => {
      let person = learner;
      if (list === 'absent') {
        person = learner.learner;
      }
      let row = [person.lastName, person.firstName, person.currentYearLevel];
      if (this.showJurisdictionalId) {
        row.push(person.jurisdictionalId);
      }
      if (list === 'absent') {
        if (this.trackMovements) {
          let movement = learner.record.get('movements.lastObject');
          if (!isEmpty(movement)) {
            let label = this.get(`attendanceLabels.${movement.code}`);
            let code = `${movement.code} (${label})`;
            row.push(movement.timeString, code, movement.notes);
          } else {
            // There should be a Movement, but there isn't one.
            row.push('', '', '');
          }
        } else {
          let label = this.get(`attendanceLabels.${learner.record.code}`);
          let code = `${learner.record.code} (${label})`;
          row.push(code, learner.record.notes);
        }
      }
      for (let i = 0; i < numEmptyColumns; i++) {
        row.push('');
      }
      data.push(row);
    });

    return data;
  }
}
